/**
 * Demolition Lab — Voronoi wall fracture runtime for /demolition.
 *
 * Mirrors yardPhysics structure (flat rt.transforms, window bridge) but does
 * NOT force-sleep after spawn — that once froze airborne rubble in place.
 * Settling uses a custom displacement/velocity sleep sweep (SleepManager is
 * retained for structural parity only). Fragment launch uses energy-derived
 * per-chunk impulses from the damage resolver, not radialImpulse.
 *
 * State machine per wall:
 *   Intact  → one static monolith box
 *   Fracture → dynamic chunks in damage radius + static remnant cells
 *   Support collapse → BFS from grounded remnant; unsupported demoted to dynamic
 */

import {
  probeCapabilities,
  FixedStepper,
  TransformBuffer,
  SleepManager,
  BodyPool,
  type BodyHandle,
  type World,
} from 'box3d-web';
import { FRACTURE_RNG_SEED, mulberry32 } from '../../services/fracture/voronoiFracture';
import {
  getDestructionProfile,
  isProfileId,
  profileWallCenter,
  profileWallHalf,
  type DestructionProfile,
  type ProfileId,
} from '../../services/destructionProfiles';
import { createInvariantMonitor } from '../../services/invariantMonitor';
import { createDemolitionEventRing } from './demolitionEventRing';
import { createDemolitionDamageLedger } from './demolitionDamageLedger';
import { addOwnedBox, createOwnedBody } from './nativeOwnership';
import { buildBasePartition, spawnMonolith } from './wallSetup';
import {
  BLAST_VOLLEY_SUB_BUDGET,
  DEFAULT_WEAPON_POWER,
  DEMO_FIXED_DT,
  DEMO_SUBSTEPS,
  MAX_CANNONBALLS,
  MAX_LIVE_CELLS_MUL,
  SLEEP_MOVE_EPS,
  SLEEP_SWEEP_INTERVAL,
} from './demolitionTuning';
import type {
  DemoTool,
  DemolitionCaps,
  DemolitionRuntime,
  HostedDemolitionRuntimeOptions,
} from './types';

const REQUIRED_DEMOLITION_CAPABILITIES = [
  'awakeBodyCount',
  'bodyCount',
  'setBodyType',
  'setAwake',
  'getLinearVelocity',
  'readSleepStates',
  'shapeMaterial',
  'convexHull',
  'contactLoad',
] as const;

/** Probe once at runtime creation; missing capabilities throw instead of silent no-ops. */
export function assertDemolitionCapabilities(world: World): void {
  const probed = probeCapabilities(world);
  const available: DemolitionCaps = {
    awakeBodyCount: probed.awakeBodyCount === true,
    bodyCount: probed.bodyCount === true,
    setBodyType: probed.setBodyType === true,
    setAwake: probed.setAwake === true,
    getLinearVelocity: probed.getLinearVelocity === true,
    readSleepStates: typeof world.readSleepStates === 'function',
    shapeMaterial: probed.shapeMaterial === true,
    convexHull: probed.convexHull === true,
    contactLoad: probed.contactLoadTelemetry === true,
  };
  const missing = REQUIRED_DEMOLITION_CAPABILITIES.filter((name) => !available[name]);
  if (missing.length > 0) {
    throw new Error(`Demolition runtime requires missing capabilities: ${missing.join(', ')}`);
  }
}

export function createDemolitionRuntime(
  profile: DestructionProfile,
  hosted: HostedDemolitionRuntimeOptions,
): DemolitionRuntime {
  const world = hosted.world;
  assertDemolitionCapabilities(world);

  const wallHalf = profileWallHalf(profile);
  const profileCenter = profileWallCenter(profile);
  const offset = hosted.worldOffset ?? { x: 0, y: 0, z: 0 };
  if (![offset.x, offset.y, offset.z].every(Number.isFinite)) {
    throw new RangeError('worldOffset must contain finite x, y, and z values');
  }
  const wallCenter = {
    x: profileCenter.x + offset.x,
    y: profileCenter.y + offset.y,
    z: profileCenter.z + offset.z,
  };
  const wallFull = { x: profile.wall.w, y: profile.wall.h, z: profile.wall.t };

  // PRISTINE partition — never mutated by pre-crack / carve refinement.
  const pristineCells = buildBasePartition(profile);
  const adjacency = new Map<number, number[]>();
  for (const cell of pristineCells) {
    adjacency.set(cell.id, cell.neighbors.slice());
  }
  let maxId = 0;
  for (const cell of pristineCells) {
    if (cell.id > maxId) maxId = cell.id;
  }

  const seedBase =
    'seed' in profile.seedSpec ? profile.seedSpec.seed : FRACTURE_RNG_SEED;

  const cannonballs: number[] = [];
  const cannonballSet = new Set<number>();
  const projectileWeapons = new Map<number, DemoTool>();
  const projectilePowers = new Map<number, number>();
  const projectileRecoveryUntil = new Map<number, number>();
  const projectileRestDwell = new Map<number, number>();
  const projectileRecoveryCount = new Map<number, number>();
  const projectileOrdinals = new Map<number, number>();
  let runtimeForPool: DemolitionRuntime | null = null;
  const embeddedProjectiles = new Map<number, { x: number; y: number; z: number }>();
  const projectileGroundTraced = new Set<number>();
  const cannonballPool = new BodyPool(world, {
    max: MAX_CANNONBALLS,
    // Kinetic tools can roll briefly after impact; rest retirement keeps the
    // pool clear before the hard transient-body age limit.
    maxAgeSec: 10,
    onEvict: (body) => {
      const bodyId = body as number;
      const index = cannonballs.indexOf(bodyId);
      if (index >= 0) cannonballs.splice(index, 1);
      cannonballSet.delete(bodyId);
      projectileWeapons.delete(bodyId);
      projectilePowers.delete(bodyId);
      projectileRecoveryUntil.delete(bodyId);
      projectileRestDwell.delete(bodyId);
      projectileRecoveryCount.delete(bodyId);
      projectileOrdinals.delete(bodyId);
      embeddedProjectiles.delete(bodyId);
      projectileGroundTraced.delete(bodyId);
      // BodyPool invokes this callback before destroying the native body. The
      // hosted owner must release its semantic actor entry at that point so
      // retire/evict cannot leave a stale world registration.
      runtimeForPool?.ownership.unregisterBody(body);
    },
  });

  const rt: DemolitionRuntime = {
    world,
    ownership: hosted.ownership,
    ownsGround: hosted.groundBody === undefined,
    stepper: new FixedStepper({ fixedDt: DEMO_FIXED_DT, substeps: DEMO_SUBSTEPS, maxStepsPerFrame: 3 }),
    buffer: new TransformBuffer(64),
    // SleepManager kept for structural parity / future use; demolition uses a
    // custom sweep so rebuilds never re-arm force-sleep on airborne rubble.
    sleep: new SleepManager(world, {
      sweepIntervalSec: SLEEP_SWEEP_INTERVAL,
      moveThreshold: SLEEP_MOVE_EPS,
    }),
    profile,
    wallFull,
    wallHalf,
    wallCenter,
    cells: pristineCells.slice(),
    cellById: new Map(pristineCells.map((cell) => [cell.id, cell])),
    pristineCells,
    pristineCellById: new Map(pristineCells.map((cell) => [cell.id, cell])),
    pristineDamage: new Map(),
    baseCells: pristineCells,
    pristineCellCount: pristineCells.length,
    baseCellCount: pristineCells.length,
    maxLiveCells: Math.max(pristineCells.length * MAX_LIVE_CELLS_MUL, MAX_LIVE_CELLS_MUL),
    nextCellId: maxId + 1,
    subRng: mulberry32(seedBase ^ 0x51b1),
    records: new Map(),
    adjacency,
    monolithBodyId: 0,
    fractured: false,
    wallVersion: 0,
    lastPartitionMs: 0,
    cannonballs,
    cannonballPool,
    cannonballSet,
    projectileWeapons,
    projectilePowers,
    projectileRecoveryUntil,
    projectileRestDwell,
    projectileRecoveryCount,
    projectileOrdinals,
    nextProjectileOrdinal: 1,
    embeddedProjectiles,
    groundBodyId: 0,
    projectileGroundTraced,
    transforms: new Float32Array(0),
    prevTransforms: new Float32Array(0),
    prevValid: false,
    bodyIds: new Int32Array(0),
    bodyIndex: new Map(),
    idsDirty: true,
    cellBodyIds: new Int32Array(0),
    cellSleepStates: new Uint8Array(0),
    staticBodyCount: 0,
    awakeCount: 0,
    bodyCountTotal: 0,
    stepMs: 0,
    fixedTime: 0,
    remnantMergeMs: 0,
    remnantMergeCount: 0,
    lastImpact: null,
    collapsedUnsupported: 0,
    removedChunks: 0,
    dynamicOrderCounter: 0,
    collapseQueue: [],
    remnantDirty: false,
    dynamicChunks: 0,
    staticCells: 0,
    subChunks: 0,
    shardParticles: 0,
    minChunkVol: 0,
    maxChunkVol: 0,
    lastCarveMs: 0,
    maxDynamicY: 0,
    fadingChunks: 0,
    sleepSample: new Float32Array(0),
    sleepSampleValid: false,
    nextSleepSweep: 0,
    sleepSlowStreak: new Map(),
    lastActivitySimTime: 0,
    posScratchIds: new Int32Array(1),
    posScratchOut: new Float32Array(7),
    tool: 'cannon',
    weaponPower: DEFAULT_WEAPON_POWER,
    charges: [],
    nextChargeId: 1,
    volleySubBudget: BLAST_VOLLEY_SUB_BUDGET,
    preCrackedCells: 0,
    lastBlast: null,
    eventRing: createDemolitionEventRing({ capacity: 512 }),
    damageLedger: createDemolitionDamageLedger(256),
    invariantMon: import.meta.env.DEV ? createInvariantMonitor() : null,
    liveCellCapHits: 0,
    lastResolve: null,
    fragmentVolumes: [],
    clampEngagements: 0,
    patchEngagements: {
      stabilizeContact: 0,
      wallFaceEject: 0,
      stallRecover: 0,
      rubbleKick: 0,
    },
    pendingPulverizedVolume: 0,
    pendingShardVolume: 0,
    pendingChunkVolume: 0,
    lastPulverizedVolume: 0,
    lastShardVolume: 0,
    lastSpawnedChunkVolume: 0,
    sessionPulverizedVolume: 0,
    residueGeneration: 0,
  };
  runtimeForPool = rt;

  addGround(rt, hosted.groundBody);
  // Construction establishes epoch zero. Only a user/runtime reset advances
  // the event and ledger epochs.
  spawnMonolith(rt, false);
  return rt;
}

/** Construct a demolition runtime in a caller-owned World. */
export async function createHostedDemolitionRuntime(
  profileId: ProfileId | string = 'concrete',
  options: HostedDemolitionRuntimeOptions,
): Promise<DemolitionRuntime> {
  const id: ProfileId = isProfileId(profileId) ? profileId : 'concrete';
  return createDemolitionRuntime(getDestructionProfile(id), options);
}

export function addGround(rt: DemolitionRuntime, suppliedGround?: BodyHandle) {
  if (suppliedGround !== undefined) {
    rt.groundBodyId = suppliedGround as number;
    rt.ownsGround = false;
    rt.staticBodyCount += 1;
    return;
  }
  const ground = createOwnedBody(rt, { type: 'static', position: [0, -0.6, 0] }, { kind: 'ground' });
  addOwnedBox(rt, ground, [60, 0.6, 60], { density: 0, friction: 0.9, restitution: 0.02 }, { kind: 'ground' });
  rt.groundBodyId = ground as number;
  rt.staticBodyCount += 1;
}
